import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Opportunity } from '../../models/OpportunityModels';
import { opportunityEndpoints } from '../endpoints/opportunityEndpoints';
import { DeclineOpportunityParams, QualifyOpportunityParams } from '../../models/APIModels';
import { AIM_SERVICE } from '../../envUtils';

const AimApiQueryCreator = () => {
  let getToken: () => Promise<string> | string = () => '';
  const setTokenGetter = (getter: () => Promise<string> | string) => {
    getToken = getter;
  };
  const api = createApi({
    reducerPath: 'opportunitiesApi',
    baseQuery: fetchBaseQuery({
      baseUrl: `${AIM_SERVICE}`,
      prepareHeaders: async (headers) => {
        const token = await getToken();
        headers.set('Authorization', `Bearer ${token}`);
        return headers;
      }
    }),
    tagTypes: ['Opportunity'],
    endpoints: (builder) => ({
      getOpportunities: builder.query<Opportunity[], null>({
        query: () => opportunityEndpoints.opportunities,
        providesTags: ['Opportunity']
      }),
      getOpportunity: builder.query<Opportunity, Pick<Opportunity, 'aimOpportunity'>>({
        query: (aimOpportunity) => opportunityEndpoints.opportunityDetails(aimOpportunity),
        //@ts-ignore
        transformResponse: (baseQueryReturnValue: Opportunity[], meta, arg) => {
          return baseQueryReturnValue[0];
        },
        providesTags: ['Opportunity']
      }),
      newOpportunity: builder.mutation<Opportunity, Opportunity>({
        query: (payload) => ({
          url: opportunityEndpoints.newOpportunity,
          method: 'POST',
          body: payload
        }),
        //@ts-ignore
        transformResponse: (baseQueryReturnValue: Opportunity[], meta, arg) => {
          return baseQueryReturnValue[0];
        },
        invalidatesTags: ['Opportunity']
      }),
      updateOpportunity: builder.mutation<Opportunity, { id: number; data: Opportunity }>({
        query: ({ ...payload }) => ({
          url: opportunityEndpoints.updateOpportunity(payload.id),
          method: 'POST',
          body: payload.data
        }),
        //@ts-ignore
        transformResponse: (baseQueryReturnValue: Opportunity[], meta, arg) => {
          return baseQueryReturnValue[0];
        },
        invalidatesTags: ['Opportunity']
      }),
      submitOpportunity: builder.mutation<Opportunity, Pick<Opportunity, 'aimOpportunity'>>({
        query: (aimOpportunity) => ({
          url: opportunityEndpoints.submitOpportunity(aimOpportunity),
          method: 'POST'
        }),
        invalidatesTags: ['Opportunity']
      }),
      cancelOpportunity: builder.mutation<Opportunity, number>({
        query: (aimOpportunity) => ({
          url: opportunityEndpoints.cancelOpportunity(aimOpportunity),
          method: 'POST'
        }),
        invalidatesTags: ['Opportunity']
      }),
      qualifyOpportunityLand: builder.mutation<Opportunity[], QualifyOpportunityParams>({
        query: ({ ...payload }) => ({
          url: opportunityEndpoints.qualifyOpportunityLand(payload.aimOpportunity),
          method: 'POST',
          body: payload.comment
        }),
        invalidatesTags: ['Opportunity']
      }),
      qualifyOpportunityCeo: builder.mutation<Opportunity, QualifyOpportunityParams>({
        query: ({ ...payload }) => ({
          url: opportunityEndpoints.qualifyOpportunityCeo(payload.aimOpportunity),
          method: 'POST',
          body: payload.comment
        }),
        invalidatesTags: ['Opportunity']
      }),
      qualifyOpportunityOperations: builder.mutation<Opportunity, QualifyOpportunityParams>({
        query: ({ ...payload }) => ({
          url: opportunityEndpoints.qualifyOpportunityOperations(payload.aimOpportunity),
          method: 'POST',
          body: payload.comment
        }),
        invalidatesTags: ['Opportunity']
      }),
      declineOpportunityLand: builder.mutation<Opportunity, DeclineOpportunityParams>({
        query: ({ ...payload }) => ({
          url: opportunityEndpoints.declineOpportunityLand(payload.aimOpportunity),
          method: 'POST',
          body: payload.data
        }),
        invalidatesTags: ['Opportunity']
      }),
      declineOpportunityCeo: builder.mutation<Opportunity, DeclineOpportunityParams>({
        query: ({ ...payload }) => ({
          url: opportunityEndpoints.declineOpportunityCeo(payload.aimOpportunity),
          method: 'POST',
          body: payload.data
        }),
        invalidatesTags: ['Opportunity']
      }),
      declineOpportunityOperations: builder.mutation<Opportunity, DeclineOpportunityParams>({
        query: ({ ...payload }) => ({
          url: opportunityEndpoints.declineOpportunityOperations(payload.aimOpportunity),
          method: 'POST',
          body: payload.data
        }),
        invalidatesTags: ['Opportunity']
      }),
      getLandQualification: builder.query<Opportunity, Pick<Opportunity, 'aimOpportunity'>>({
        query: (aimOpportunity) => opportunityEndpoints.getLandQualification(aimOpportunity),
        providesTags: ['Opportunity']
      }),
      getCeoQualification: builder.query<Opportunity, Pick<Opportunity, 'aimOpportunity'>>({
        query: (aimOpportunity) => opportunityEndpoints.getCeoQualification(aimOpportunity),
        providesTags: ['Opportunity']
      }),
      getOperationsQualification: builder.query<Opportunity, Pick<Opportunity, 'aimOpportunity'>>({
        query: (aimOpportunity) => opportunityEndpoints.getOperationsQualification(aimOpportunity),
        providesTags: ['Opportunity']
      }),
      raiseOpportunityPriority: builder.mutation<Opportunity, Pick<Opportunity, 'aimOpportunity'>>({
        query: (aimOpportunity) => ({
          url: opportunityEndpoints.raiseOpportunityPriority(aimOpportunity),
          method: 'GET'
        }),
        invalidatesTags: ['Opportunity']
      }),
      lowerOpportunityPriority: builder.mutation<Opportunity, Pick<Opportunity, 'aimOpportunity'>>({
        query: (aimOpportunity) => ({
          url: opportunityEndpoints.lowerOpportunityPriority(aimOpportunity),
          method: 'GET'
        }),
        invalidatesTags: ['Opportunity']
      }),
      markOpportunityMapped: builder.mutation<Opportunity, Pick<Opportunity, 'aimOpportunity'>>({
        query: (aimOpportunity) => ({
          url: opportunityEndpoints.markOpportunityMapped(aimOpportunity),
          method: 'GET'
        }),
        invalidatesTags: ['Opportunity']
      })
    })
  });
  return { hooks: api, setTokenGetter };
};

/**
 * An RTK Query service providing React hooks for opportunity management endpoints in the AIM platform, including querying, creating, updating, submitting, declining, qualifying, and prioritizing opportunities, built with `fetchBaseQuery` and automatic Bearer-token injection.
 */
export const OpportunityService = AimApiQueryCreator();
export const {
  useGetOpportunitiesQuery,
  useLazyGetOpportunityQuery,
  useNewOpportunityMutation,
  useUpdateOpportunityMutation,
  useSubmitOpportunityMutation,
  useCancelOpportunityMutation,
  useQualifyOpportunityLandMutation,
  useQualifyOpportunityCeoMutation,
  useQualifyOpportunityOperationsMutation,
  useDeclineOpportunityLandMutation,
  useDeclineOpportunityCeoMutation,
  useDeclineOpportunityOperationsMutation,
  useLazyGetLandQualificationQuery,
  useLazyGetCeoQualificationQuery,
  useLazyGetOperationsQualificationQuery,
  useRaiseOpportunityPriorityMutation,
  useLowerOpportunityPriorityMutation,
  useMarkOpportunityMappedMutation
} = OpportunityService.hooks;
